import {
  Component,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalRef, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ChatbotService } from '@pt/api-client';
import { ChatbotAskQuestionRequest } from 'it/primatoo/openapi/api/org/schemas/chatbot/ChatbotAskQuestionRequest';
import { Language } from 'it/primatoo/openapi/api/common/schemas/Language';
import { ChatbotAskQuestionResponse } from 'it/primatoo/openapi/api/org/schemas/chatbot/ChatbotAskQuestionResponse';
import { ChatbotMessages, USER_CHATBOT } from './models/chatbot-message';
import { PtChatbotGenericMessageComponent } from './messages/pt-chatbot-generic-message.component';
import { ChatbotMessageType } from './models/chatbot-message-type';
import { Observable } from 'rxjs';
import { ChatbotSendSectionReactionRequest } from 'it/primatoo/openapi/api/org/schemas/chatbot/ChatbotSendSectionReactionRequest';
import { ChatbotSendReactionRequest } from 'it/primatoo/openapi/api/org/schemas/chatbot/ChatbotSendReactionRequest';
import { IoniconsFilled } from '@pt/pt-models';
import { PtSharedModule } from '@pt/pt-shared';

@Component({
  selector: 'pt-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    NgbTooltip,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    PtChatbotGenericMessageComponent,
    PtSharedModule,
  ],
  templateUrl: './pt-chatbot.component.html',
  styleUrls: ['./pt-chatbot.component.css'],
})
export class PtChatbotComponent implements OnDestroy {
  @Input() username!: string;
  @ViewChild('chatbotDialog', { static: true })
  chatbotDialog?: TemplateRef<unknown>;
  private modalRef?: NgbModalRef;
  public messages: ChatbotMessages<any>[] = [];
  public MT = ChatbotMessageType;
  public loading = false;
  public expand = false;
  public currentQuestion?: string; //  = 'Give me a list of gdpr rules'; //'How does the GDPR define the concept of a Data Protection Impact Assessment';

  public reduceIcon = IoniconsFilled.arrowDown;
  public expandIcon = IoniconsFilled.arrowUp;

  constructor(
    private modalService: NgbModal,
    private i18n: TranslateService,
    private chatbotService: ChatbotService
  ) {}

  ngOnDestroy(): void {
    this.close();
  }

  sentReaction(
    reaction: ChatbotSendReactionRequest,
    msg: ChatbotAskQuestionResponse
  ) {
    this.loading = true;
    this.chatbotService.sendReaction(msg.uuid, reaction).subscribe(
      () => {
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  sentSectionReaction(
    reaction: ChatbotSendSectionReactionRequest,
    msg: ChatbotAskQuestionResponse
  ) {
    this.loading = true;
    this.chatbotService.sendSectionReaction(msg.uuid, reaction).subscribe(
      () => {
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  sendAskQuestion(question?: string) {
    if (question) {
      const askQuestion: ChatbotAskQuestionRequest = {
        question,
        lang: Language.en, // Attualmente unica lingua supportata
      };
      this.messages.push(
        ChatbotMessages.fromChatbotAskQuestionRequest(
          askQuestion,
          this.username
        )
      );
      this.loading = true;
      this.currentQuestion = '';
      this.chatbotService.askQuestion(askQuestion).subscribe({
        next: (response: ChatbotAskQuestionResponse) => {
          this.loading = false;
          this.messages.push(
            ChatbotMessages.fromChatbotAskQuestionResponse(
              response,
              USER_CHATBOT
            )
          );
        },
        error: () => {
          this.loading = false;
          this.generateGenericMessage(
            'INFO',
            this.i18n.stream('MESSAGE.CHAT_TIMEOUT')
          );
        },
        complete: () => {},
      });
    }
  }

  private generateGenericMessage(
    type: 'INFO' | 'ERROR',
    message?: Observable<string>
  ) {
    this.messages.push(
      ChatbotMessages.createGeneric(
        {
          type: type,
          message: message,
        },
        USER_CHATBOT
      )
    );
  }

  close() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
      this.messages = [];
    }
  }

  open() {
    this.close();
    this.messages = [];
    // this.generateGenericMessage('INFO');
    this.generateChatBotMessage(
      this.i18n.stream('MESSAGE.CHATBOT_HELLO', { value: this.username })
    );

    this.modalRef = this.modalService.open(this.chatbotDialog, {
      centered: false,
      backdrop: 'static',
      size: 'chatbot', // cerca classe modal-chatbot
    });
  }

  toggleExpandDialog() {
    if (this.modalRef) {
      this.expand = !this.expand;
      this.modalRef.update({
        size: this.expand ? 'chatbot-full' : 'chatbot',
      });
    }
  }

  private generateChatBotMessage(observable: Observable<string>) {
    this.messages.push(ChatbotMessages.fromChatbotMessage(observable));
  }
}
